<template>
  <div class="prompt-tuning">
    <div class="tuning-header">
      <div>
        <h3>Fine-tuning del Prompt</h3>
        <p class="tuning-desc">
          Agrega ejemplos de conversación para ajustar el comportamiento del agente.
          Cada ejemplo le enseña al agente cómo responder ante situaciones específicas.
        </p>
      </div>
      <Button label="Nuevo ejemplo" icon="pi pi-plus" size="small" @click="openCreate" />
    </div>

    <!-- Lista de ejemplos -->
    <div v-if="loading" class="loading-msg">
      <i class="pi pi-spin pi-spinner" /> Cargando ejemplos...
    </div>

    <div v-else-if="examples.length === 0" class="empty-examples">
      <i class="pi pi-lightbulb" style="font-size:2rem;color:#d1d5db" />
      <p>No hay ejemplos de fine-tuning aún.</p>
      <p class="empty-hint">
        Los ejemplos se inyectan en el prompt del sistema como "few-shot examples",
        guiando al agente sobre el tono, formato y contenido esperado.
      </p>
      <Button label="Agregar primer ejemplo" icon="pi pi-plus" outlined @click="openCreate" />
    </div>

    <div v-else class="examples-list">
      <div
        v-for="(ex, idx) in examples"
        :key="ex.id"
        class="example-card"
        :class="{ inactive: !ex.is_active }"
      >
        <div class="example-header">
          <div class="example-meta">
            <span class="example-num">#{{ idx + 1 }}</span>
            <span v-if="ex.label" class="example-label">{{ ex.label }}</span>
            <Tag v-if="ex.category" :value="ex.category" severity="secondary" size="small" />
            <Tag v-if="!ex.is_active" value="Inactivo" severity="secondary" size="small" />
          </div>
          <div class="example-actions">
            <Button icon="pi pi-pencil" text rounded size="small" @click="openEdit(ex)" />
            <Button
              :icon="ex.is_active ? 'pi pi-eye-slash' : 'pi pi-eye'"
              text rounded size="small"
              :severity="ex.is_active ? 'warning' : 'success'"
              @click="toggleActive(ex)"
            />
            <Button icon="pi pi-trash" text rounded size="small" severity="danger" @click="confirmDelete(ex)" />
          </div>
        </div>

        <div class="example-body">
          <div class="example-side user-side">
            <div class="side-label"><i class="pi pi-user" /> Usuario</div>
            <div class="side-content">{{ ex.user_input }}</div>
          </div>
          <div class="example-arrow"><i class="pi pi-arrow-right" /></div>
          <div class="example-side agent-side">
            <div class="side-label"><i class="pi pi-android" /> Agente</div>
            <div class="side-content">{{ ex.expected_output }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog crear/editar -->
    <Dialog
      v-model:visible="showForm"
      :header="editingExample ? 'Editar ejemplo' : 'Nuevo ejemplo de fine-tuning'"
      modal
      :style="{ width: '700px' }"
      :breakpoints="{ '768px': '95vw' }"
    >
      <div class="example-form">
        <div class="field">
          <label>Etiqueta <span class="hint">(opcional, para identificar el ejemplo)</span></label>
          <InputText v-model="form.label" placeholder="Ej: Respuesta formal, Manejo de errores..." class="w-full" />
        </div>

        <div class="field">
          <label>Categoría</label>
          <Dropdown
            v-model="form.category"
            :options="categories"
            optionLabel="label"
            optionValue="value"
            placeholder="Sin categoría"
            showClear
            class="w-full"
          />
        </div>

        <div class="field">
          <label>Mensaje del usuario <span class="required">*</span></label>
          <Textarea
            v-model="form.user_input"
            placeholder="Escribe el mensaje de ejemplo que enviaría el usuario..."
            rows="3"
            class="w-full"
            autoResize
          />
          <small class="p-error" v-if="errors.user_input">{{ errors.user_input }}</small>
        </div>

        <div class="field">
          <label>Respuesta esperada del agente <span class="required">*</span></label>
          <Textarea
            v-model="form.expected_output"
            placeholder="Escribe cómo debería responder el agente idealmente..."
            rows="5"
            class="w-full"
            autoResize
          />
          <small class="p-error" v-if="errors.expected_output">{{ errors.expected_output }}</small>
        </div>

        <div class="form-tip">
          <i class="pi pi-info-circle" />
          Este ejemplo se inyectará en el prompt del sistema como guía de comportamiento.
          Sé específico y usa el tono/formato que quieres que el agente replique.
        </div>
      </div>

      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showForm = false" />
        <Button
          :label="editingExample ? 'Guardar cambios' : 'Agregar ejemplo'"
          icon="pi pi-check"
          :loading="saving"
          @click="saveExample"
        />
      </template>
    </Dialog>

    <!-- Confirm delete -->
    <Dialog v-model:visible="showDeleteConfirm" header="Eliminar ejemplo" modal :style="{ width: '380px' }">
      <p>¿Eliminar el ejemplo <strong>{{ deletingExample?.label || '#' + (examples.indexOf(deletingExample) + 1) }}</strong>?</p>
      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showDeleteConfirm = false" />
        <Button label="Eliminar" severity="danger" :loading="deleting" @click="executeDelete" />
      </template>
    </Dialog>

    <Toast />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Dropdown from 'primevue/dropdown';
import Toast from 'primevue/toast';
import api from '../../services/api';

const props = defineProps({
  agentId: { type: Number, required: true },
});

const toast = useToast();
const examples = ref([]);
const loading = ref(false);
const showForm = ref(false);
const editingExample = ref(null);
const saving = ref(false);
const showDeleteConfirm = ref(false);
const deletingExample = ref(null);
const deleting = ref(false);
const errors = ref({});

const categories = [
  { label: 'Tono y estilo', value: 'tono' },
  { label: 'Formato de respuesta', value: 'formato' },
  { label: 'Manejo de errores', value: 'errores' },
  { label: 'Dominio específico', value: 'dominio' },
  { label: 'Restricciones', value: 'restricciones' },
  { label: 'Saludos y despedidas', value: 'saludos' },
];

const form = ref({ label: '', category: null, user_input: '', expected_output: '' });

const loadExamples = async () => {
  loading.value = true;
  try {
    const res = await api.get(`/agents/${props.agentId}/examples/`);
    examples.value = res.data;
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
};

const openCreate = () => {
  editingExample.value = null;
  form.value = { label: '', category: null, user_input: '', expected_output: '' };
  errors.value = {};
  showForm.value = true;
};

const openEdit = (ex) => {
  editingExample.value = ex;
  form.value = { label: ex.label || '', category: ex.category || null, user_input: ex.user_input, expected_output: ex.expected_output };
  errors.value = {};
  showForm.value = true;
};

const validate = () => {
  errors.value = {};
  if (!form.value.user_input.trim()) errors.value.user_input = 'El mensaje del usuario es obligatorio';
  if (!form.value.expected_output.trim()) errors.value.expected_output = 'La respuesta esperada es obligatoria';
  return Object.keys(errors.value).length === 0;
};

const saveExample = async () => {
  if (!validate()) return;
  saving.value = true;
  try {
    const payload = {
      user_input: form.value.user_input.trim(),
      expected_output: form.value.expected_output.trim(),
      label: form.value.label.trim() || null,
      category: form.value.category || null,
      sort_order: editingExample.value?.sort_order ?? examples.value.length,
    };
    if (editingExample.value) {
      await api.put(`/agents/${props.agentId}/examples/${editingExample.value.id}`, payload);
      toast.add({ severity: 'success', summary: 'Actualizado', detail: 'Ejemplo actualizado', life: 2500 });
    } else {
      await api.post(`/agents/${props.agentId}/examples/`, payload);
      toast.add({ severity: 'success', summary: 'Agregado', detail: 'Ejemplo agregado al fine-tuning', life: 2500 });
    }
    showForm.value = false;
    await loadExamples();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e.response?.data?.detail || e.message, life: 3000 });
  } finally {
    saving.value = false;
  }
};

const toggleActive = async (ex) => {
  try {
    await api.put(`/agents/${props.agentId}/examples/${ex.id}`, { is_active: !ex.is_active });
    ex.is_active = !ex.is_active;
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo cambiar el estado', life: 3000 });
  }
};

const confirmDelete = (ex) => {
  deletingExample.value = ex;
  showDeleteConfirm.value = true;
};

const executeDelete = async () => {
  deleting.value = true;
  try {
    await api.delete(`/agents/${props.agentId}/examples/${deletingExample.value.id}`);
    showDeleteConfirm.value = false;
    await loadExamples();
    toast.add({ severity: 'success', summary: 'Eliminado', detail: 'Ejemplo eliminado', life: 2500 });
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar', life: 3000 });
  } finally {
    deleting.value = false;
  }
};

onMounted(loadExamples);
</script>

<style scoped>
.prompt-tuning { display: flex; flex-direction: column; gap: 1.25rem; }

.tuning-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
.tuning-header h3 { margin: 0; color: #1e293b; font-size: 1rem; }
.tuning-desc { margin: 0.25rem 0 0; font-size: 0.82rem; color: #6b7280; max-width: 500px; }

.loading-msg, .empty-examples { text-align: center; padding: 2rem; color: #9ca3af; display: flex; flex-direction: column; align-items: center; gap: 0.5rem; }
.empty-hint { font-size: 0.8rem; max-width: 400px; }

.examples-list { display: flex; flex-direction: column; gap: 0.75rem; }

.example-card {
  border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden;
  transition: box-shadow 0.15s;
}
.example-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
.example-card.inactive { opacity: 0.6; }

.example-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.6rem 0.875rem; background: #f8fafc; border-bottom: 1px solid #f1f5f9;
}
.example-meta { display: flex; align-items: center; gap: 0.5rem; }
.example-num { font-size: 0.75rem; font-weight: 700; color: #9ca3af; }
.example-label { font-size: 0.82rem; font-weight: 600; color: #374151; }
.example-actions { display: flex; gap: 0.1rem; }

.example-body {
  display: grid; grid-template-columns: 1fr auto 1fr;
  gap: 0; align-items: stretch;
}
.example-side { padding: 0.875rem; }
.user-side { background: #f0f9ff; border-right: 1px solid #e0f2fe; }
.agent-side { background: #f0fdf4; }
.example-arrow {
  display: flex; align-items: center; justify-content: center;
  padding: 0 0.5rem; color: #9ca3af; font-size: 0.9rem;
  background: white; border-left: 1px solid #e5e7eb; border-right: 1px solid #e5e7eb;
}
.side-label { font-size: 0.72rem; font-weight: 700; color: #6b7280; margin-bottom: 0.4rem; display: flex; align-items: center; gap: 0.3rem; }
.side-content { font-size: 0.85rem; color: #374151; line-height: 1.5; white-space: pre-wrap; }

/* Form */
.example-form { display: flex; flex-direction: column; gap: 1rem; }
.field { display: flex; flex-direction: column; gap: 0.3rem; }
.field label { font-weight: 600; font-size: 0.875rem; color: #374151; }
.required { color: #ef4444; }
.hint { font-weight: 400; font-size: 0.75rem; color: #9ca3af; }
.form-tip {
  display: flex; align-items: flex-start; gap: 0.5rem;
  padding: 0.75rem; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px;
  font-size: 0.8rem; color: #92400e;
}
.w-full { width: 100%; }
</style>
